import { isStagingAsaphHost } from '@/helpers/isStagingHostname';
import { mergeDeepWith } from 'ramda';

import { Environment, Environments } from '.';

const environmentFlag = (env: Environments): string => {
	switch (env) {
		case Environments.PRODUCTION:
			return 'PRODUCTION';
		case Environments.STAGING:
			return 'STAGING';
		default:
			return 'LOCAL';
	}
};

export const getRequiredStringEnvVar = (varName: string): string => {
	const value = getStringEnvVar(varName);
	if (!value) throw new Error(`Missing string value from: ${varName}`);
	return value;
};

export function getStringEnvVar(varName: string, fallback = ''): string {
	const value = import.meta.env[varName];
	// If the string has length, return the string else return undefined
	return value == undefined
		? fallback
		: value.length === 0
		? fallback
		: value;
}

function getEnvironmentValueStrict(envOverride?: string): Environments {
	const envFlag =
		envOverride ||
		(isStagingAsaphHost()
			? environmentFlag(Environments.STAGING)
			: getRequiredStringEnvVar('VITE_ENVIRONMENT'));
	if (!Object.values(Environments).includes(envFlag)) {
		throw new Error(
			`Environment Variable VITE_ENVIRONMENT needs to be one of ${Object.values(
				Environments,
			).join(', ')}`,
		);
	}

	switch (true) {
		case envFlag === 'PRODUCTION':
			return Environments.PRODUCTION;
		case envFlag === 'STAGING':
			return Environments.STAGING;
		default:
			return Environments.LOCAL;
	}
}

const pickTruthy = (l, r) => (!!r ? r : l);

export const ENV_DEFAULTS: Pick<Environment, 'version' | 'isLoading'> = {
	version: '1.61.0',
	isLoading: false,
};

const REQUIRED_API_VERSION = '1.0.2';

export const getEnvironment = (envOverride?: string): Environment => {
	const env = getEnvironmentValueStrict(envOverride);

	const overrides: Partial<Environment> = {
		client: {
			url: getStringEnvVar('VITE_CLIENT_URL', undefined),
		},
		api: {
			url: getStringEnvVar('VITE_API_URL', undefined),
			wsUrl: getStringEnvVar('VITE_API_WS_URL', undefined),
			version: getStringEnvVar('VITE_API_VERSION', undefined),
		},
	};

	const environmentConfigMap: Record<Environments, Environment> = {
		[Environments.LOCAL]: mergeDeepWith(
			pickTruthy,
			{
				...ENV_DEFAULTS,
				environment: Environments.LOCAL,
				environmentString: '[LOCAL]',
				mediaDomainRoot: 'https://media.app.asaph.io/',
				client: {
					url: 'http://localhost:3000',
				},
				api: {
					url: 'http://localhost:4000',
					wsUrl: 'ws://localhost:4000',
					version: REQUIRED_API_VERSION,
				},
				analytics: {
					url: 'http://localhost:3001',
					websiteId: '0bd30ee1-6a6f-433a-ae74-0d041ffa7efb',
					debug: true,
				},
				integrations: {
					chargebee: {
						domain: 'asaph-test',
						planId: 'Solo',
					},
					analytics: {
						enabled: true,
						measurementId: 'G-QCYNMQ1YQ3',
					},
					intercom: {
						appId: 'wmwldqhe',
					},
					onesignal: {
						appId: 'efc11b71-ca07-4895-be38-ec3d51e0c138',
					},
				},
			},
			overrides,
		),
		[Environments.STAGING]: mergeDeepWith(
			pickTruthy,
			{
				...ENV_DEFAULTS,
				environment: Environments.STAGING,
				environmentString: '[STAGING]',
				mediaDomainRoot: 'https://media.app.asaph.io/',
				client: {
					url: 'https://staging.app.asaph.io',
				},
				api: {
					url: 'https://asaph-server.herokuapp.com',
					wsUrl: 'wss://asaph-server.herokuapp.com',
					version: REQUIRED_API_VERSION,
				},
				analytics: {
					url: 'https://analytics.staging.asaph.io',
					websiteId: 'b632d79c-a048-4b5c-a8d1-5f880e9bc081',
					debug: true,
				},
				integrations: {
					chargebee: {
						domain: 'asaph-test',
						planId: 'Solo',
					},
					analytics: {
						enabled: true,
						measurementId: 'G-FLNJWMKWY6',
					},
					intercom: {
						appId: 'wmwldqhe',
					},
					onesignal: {
						appId: '51bd47dc-739d-414b-ac29-e35bbd0c8fb0',
					},
				},
			},
			overrides,
		),
		[Environments.PRODUCTION]: mergeDeepWith(
			pickTruthy,
			{
				...ENV_DEFAULTS,
				environment: Environments.PRODUCTION,
				environmentString: '',
				mediaDomainRoot: 'https://media.app.asaph.io/',
				client: {
					url: 'https://app.asaph.io',
				},
				api: {
					url: 'https://api.asaph.io',
					wsUrl: 'wss://api.asaph.io',
					version: REQUIRED_API_VERSION,
				},
				analytics: {
					url: 'https://analytics.asaph.io',
					websiteId: 'bac7e255-a2ee-4fba-b88f-0272d493fb4b',
				},
				integrations: {
					chargebee: {
						domain: 'asaph',
						planId: 'Solo',
					},
					analytics: {
						enabled: true,
						measurementId: 'G-DQ8RCDSQ0Q',
					},
					intercom: {
						appId: 'zoq6snc5',
					},
					onesignal: {
						appId: 'efc11b71-ca07-4895-be38-ec3d51e0c138',
					},
				},
			},
			overrides,
		),
	};
	return environmentConfigMap[env];
};
